import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { FormulaCardComponent } from '@shared/components/formula/formula-card/formula-card.component';
import { TipoArredondamentoEnum } from '@shared/enums/formula/tipo-arredondamento.enum';
import { TipoFormulaEnum } from '@shared/enums/formula/tipo-formula.enum';
import { AbntPipe } from '@shared/pipes/abnt.pipe';
import { FormulaCatalogoItemStruct } from '@shared/services/structs/formula/formula-catalogo-item.struct';
import { VariavelStruct } from '@shared/services/structs/formula/variavel.struct';
import { NFeEntradaFormacaoPrecosMemoriaStruct } from '@shared/services/structs/orquestrador-compras/nfe-entrada-formacao-precos-memoria.struct';
import { NFeEntradaFormacaoPrecosVariavelStruct } from '@shared/services/structs/orquestrador-compras/nfe-entrada-formacao-precos-variavel.struct';
import { FormacaoPrecosMemoriaCalculoDialogData } from './formacao-precos-memoria-calculo-dialog-data';

/** Linha do quadro "Valores utilizados": a variável da fórmula, como a tela a descreve, e o valor usado. */
interface ValorUtilizado {
  chave: string;
  descricao: string;
  valor: number | null;
  isAplicavel: boolean;
}

/**
 * Mostra a fórmula efetiva do preço de custo e os valores que o motor da Suprimentos usou no item,
 * incluindo o detalhe dos impostos recuperáveis e o arredondamento aplicado ao resultado.
 */
@Component({
  selector: 'app-formacao-precos-memoria-calculo-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule, FormulaCardComponent, AbntPipe],
  templateUrl: './formacao-precos-memoria-calculo-dialog.component.html',
  styleUrls: ['./formacao-precos-memoria-calculo-dialog.component.css'],
})
export class FormacaoPrecosMemoriaCalculoDialogComponent {
  readonly formulaCard: FormulaCatalogoItemStruct | null;
  readonly valoresPrecoCusto: ValorUtilizado[];
  readonly valoresImpostosRecuperaveis: ValorUtilizado[];
  private readonly variavelPorChave: Map<string, NFeEntradaFormacaoPrecosVariavelStruct>;

  constructor(
    private dialogRef: MatDialogRef<FormacaoPrecosMemoriaCalculoDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: FormacaoPrecosMemoriaCalculoDialogData) {
    this.variavelPorChave = new Map(data.variaveis.map(variavel => [variavel.chave.toLowerCase(), variavel] as const));
    this.formulaCard = this.montarFormulaCard();
    this.valoresPrecoCusto = this.listarValores(data.memoriaPrecoCusto);
    this.valoresImpostosRecuperaveis = this.listarValores(data.memoriaImpostosRecuperaveis);
  }

  get descricaoArredondamento(): string {
    const memoria = this.data.memoriaPrecoCusto;
    if (!memoria) return '';
    const tipo = memoria.tipoArredondamento === TipoArredondamentoEnum.Truncar ? 'Truncado' : 'Arredondado';
    return `${tipo} em ${memoria.casasDecimais} casas decimais`;
  }

  fechar(): void {
    this.dialogRef.close();
  }

  /** O card recebe a expressão efetiva como único modelo, inclusive quando a empresa a personalizou. */
  private montarFormulaCard(): FormulaCatalogoItemStruct | null {
    const formula = this.data.formula;
    if (!formula) return null;
    const saida = this.variavelPorChave.get(formula.chaveSaida.toLowerCase());
    const chaveModelo = formula.chaveFormulaModelo ?? `${formula.chaveFormula}.efetiva`;
    const variaveis = this.data.variaveis.map(variavel => this.mapearVariavel(variavel));
    return {
      chaveFormula: formula.chaveFormula,
      descricao: saida?.descricao ?? formula.chaveSaida,
      isPersonalizavel: false,
      variavelPrincipal: saida ? this.mapearVariavel(saida) : null,
      variaveis,
      variaveisPermitidas: variaveis,
      modelos: [{
        chaveFormulaModelo: chaveModelo,
        modelo: chaveModelo.split('.').pop() ?? '',
        nome: formula.tipoFormula === TipoFormulaEnum.Personalizada ? 'Personalizada' : null,
        descricao: null,
        expressao: this.data.memoriaPrecoCusto?.expressaoEfetiva ?? formula.expressao,
        tipoArredondamento: formula.tipoArredondamento,
        casasDecimais: formula.casasDecimais,
        isModeloPadraoSistema: formula.tipoFormula === TipoFormulaEnum.ModeloPadrao,
      }],
      chaveFormulaModeloEmpresa: chaveModelo,
    };
  }

  private listarValores(memoria: NFeEntradaFormacaoPrecosMemoriaStruct | null): ValorUtilizado[] {
    return (memoria?.operandos ?? []).map(operando => ({
      chave: operando.chave,
      descricao: this.variavelPorChave.get(operando.chave.toLowerCase())?.descricao ?? operando.chave,
      valor: operando.valor,
      isAplicavel: operando.isAplicavel,
    }));
  }

  private mapearVariavel(variavel: NFeEntradaFormacaoPrecosVariavelStruct): VariavelStruct {
    return {
      nome: variavel.nome,
      chave: variavel.chave,
      pathTagXml: null,
      descricao: variavel.descricao,
      observacao: null,
      tipoDadoVariavel: variavel.tipoDadoVariavel,
      tipoEscopoVariavel: variavel.tipoEscopoVariavel,
      tipoOrigemVariavel: variavel.tipoOrigemVariavel ?? undefined,
      isVariavelGrupo: variavel.isVariavelGrupo,
    };
  }
}
